import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "../../components/ui/dialog";
import {
    Users,
    Clock,
    Activity,
    Loader2,
    Pencil,
    Trash2,
    Plus,
    CalendarDays,
    CalendarX,
    Save,
    Umbrella,
    AlarmClock
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { LiveClock } from "../../components/workSession/LiveClock";
import {
    getAdminOverview,
    getAdminAgentMonthly,
    upsertWorkSession,
    deleteWorkSession,
    addDayOff,
    deleteDayOff,
    setAgentAllowance,
    setDefaultAllowance,
    setWorkSchedule
} from "../../services/crmServices/workSessionService";

function fmtDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtHours(totalSeconds) {
    const h = (totalSeconds || 0) / 3600;
    return `${(Math.round(h * 100) / 100).toFixed(1)}h`;
}

function fmtTime(iso, tz) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString("en-GB", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return new Date(iso).toLocaleTimeString();
    }
}

function fmtDateLabel(dateStr) {
    try {
        const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString("en-GB", {
            timeZone: "UTC",
            weekday: "short",
            day: "2-digit",
            month: "short"
        });
    } catch {
        return dateStr;
    }
}

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

function fmtMonthLabel(monthStr) {
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return monthStr || "";
    const [y, m] = monthStr.split("-").map((n) => parseInt(n, 10));
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", {
        timeZone: "UTC",
        month: "long",
        year: "numeric"
    });
}

function buildMonthOptions() {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        opts.push({ value, label });
    }
    return opts;
}

const COLOR_MAP = {
    red: "bg-red-50 text-red-600",
    green: "bg-green-50 text-green-600",
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600"
};

const StatCard = ({ icon: Icon, label, value, color = "red" }) => {
    const [bg, text] = (COLOR_MAP[color] || COLOR_MAP.red).split(" ");
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`h-5 w-5 ${text}`} />
            </div>
            <div>
                <div className="text-xs text-gray-500">{label}</div>
                <div className="text-xl font-bold text-gray-900 leading-tight">{value}</div>
            </div>
        </div>
    );
};

/* --------------------------- Edit session modal --------------------------- */
function EditSessionModal({ open, onClose, agentId, session, onSaved }) {
    const [startedAt, setStartedAt] = useState("");
    const [endedAt, setEndedAt] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setStartedAt(toLocalInput(session?.startedAt));
            setEndedAt(toLocalInput(session?.endedAt));
        }
    }, [open, session]);

    const handleSave = async () => {
        if (!startedAt) {
            toast.error("Start time is required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                agentId,
                sessionId: session?._id || undefined,
                startedAt: new Date(startedAt).toISOString(),
                endedAt: endedAt ? new Date(endedAt).toISOString() : null
            };
            await upsertWorkSession(payload);
            toast.success("Work session saved.");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not save session.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{session?._id ? "Edit work session" : "Add work session"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label htmlFor="startedAt">Start</Label>
                        <Input
                            id="startedAt"
                            type="datetime-local"
                            value={startedAt}
                            onChange={(e) => setStartedAt(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="endedAt">End (leave empty to keep open)</Label>
                        <Input
                            id="endedAt"
                            type="datetime-local"
                            value={endedAt}
                            onChange={(e) => setEndedAt(e.target.value)}
                        />
                    </div>
                    <p className="text-[11px] text-gray-400">
                        Times use your browser's local timezone. One session per day per agent.
                    </p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* --------------------------- Agent detail modal --------------------------- */
function AgentDetailModal({ open, onClose, agentId, month }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editSession, setEditSession] = useState(null);

    const load = useCallback(async () => {
        if (!agentId) return;
        setLoading(true);
        try {
            const res = await getAdminAgentMonthly(agentId, month);
            setDetail(res?.data || null);
        } catch (e) {
            toast.error("Could not load agent detail.");
        } finally {
            setLoading(false);
        }
    }, [agentId, month]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const handleDelete = async (sessionId) => {
        if (!window.confirm("Delete this work session? This cannot be undone.")) return;
        try {
            await deleteWorkSession(sessionId);
            toast.success("Session deleted.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not delete session.");
        }
    };

    const tz = detail?.timezone;
    const agentName = detail?.agent
        ? `${detail.agent.firstName || ""} ${detail.agent.lastName || ""}`.trim() || detail.agent.email
        : "Agent";
    const dayOff = detail?.dayOffSummary;

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{agentName} — work log</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                        </div>
                    ) : (
                        <>
                            {detail?.schedule && (
                                <div className="text-xs text-gray-500 mb-3">
                                    Scheduled hours: <span className="font-medium text-gray-700">{detail.schedule.start}–{detail.schedule.end}</span>
                                    {detail.schedule.graceMinutes ? ` (grace ${detail.schedule.graceMinutes}m)` : ""}
                                </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                                <StatCard icon={Clock} label="Total hours" value={fmtHours(detail?.totalSeconds || 0)} />
                                <StatCard icon={CalendarDays} label="Days worked" value={detail?.daysWorked || 0} />
                                <StatCard
                                    icon={AlarmClock}
                                    label="Late days"
                                    value={detail?.lateDays || 0}
                                    color={detail?.lateDays ? "red" : "emerald"}
                                />
                                <StatCard icon={Activity} label="Avg start" value={detail?.avgStart || "—"} color="emerald" />
                                <StatCard icon={Activity} label="Avg end" value={detail?.avgEnd || "—"} color="emerald" />
                            </div>

                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">Daily sessions</span>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setEditSession(null);
                                        setEditOpen(true);
                                    }}
                                >
                                    <Plus className="h-4 w-4 mr-1" /> Add day
                                </Button>
                            </div>

                            <div className="border border-gray-100 rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Day</TableHead>
                                            <TableHead>Start</TableHead>
                                            <TableHead>End</TableHead>
                                            <TableHead className="text-right">Duration</TableHead>
                                            <TableHead className="text-center">Notes</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(detail?.days || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                                                    No work recorded this month.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            [...(detail?.days || [])]
                                                .sort((a, b) => (a.date < b.date ? 1 : -1))
                                                .map((d) => (
                                                    <TableRow key={d._id || d.date}>
                                                        <TableCell className="font-medium text-gray-800">
                                                            {fmtDateLabel(d.date)}
                                                        </TableCell>
                                                        <TableCell>{fmtTime(d.startedAt, tz)}</TableCell>
                                                        <TableCell>
                                                            {d.active ? (
                                                                <span className="text-green-600 font-medium">In progress</span>
                                                            ) : (
                                                                fmtTime(d.endedAt, tz)
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right tabular-nums">
                                                            {fmtDuration(d.durationSeconds)}
                                                        </TableCell>
                                                        <TableCell className="text-center space-x-1">
                                                            {d.late && (
                                                                <Badge className="bg-red-100 text-red-700">Late {d.lateMinutes}m</Badge>
                                                            )}
                                                            {d.earlyLeave && (
                                                                <Badge className="bg-orange-100 text-orange-700">Early {d.earlyMinutes}m</Badge>
                                                            )}
                                                            {d.autoClosed && (
                                                                <Badge className="bg-amber-100 text-amber-700">Auto-closed</Badge>
                                                            )}
                                                            {d.createdByAdmin && !d.autoClosed && (
                                                                <Badge className="bg-purple-100 text-purple-700">Adjusted</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right whitespace-nowrap">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => {
                                                                    setEditSession(d);
                                                                    setEditOpen(true);
                                                                }}
                                                                title="Edit"
                                                            >
                                                                <Pencil className="h-4 w-4 text-gray-500" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleDelete(d._id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="h-4 w-4 text-red-600" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            <DayOffSection agentId={agentId} dayOff={dayOff} onChanged={load} />
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <EditSessionModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                agentId={agentId}
                session={editSession}
                onSaved={load}
            />
        </>
    );
}

/* --------------------------- Day-off section (inside agent detail) --------------------------- */
function DayOffSection({ agentId, dayOff, onChanged }) {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [note, setNote] = useState("");
    const [adding, setAdding] = useState(false);
    const [allowanceInput, setAllowanceInput] = useState("");
    const [savingAllowance, setSavingAllowance] = useState(false);

    useEffect(() => {
        if (dayOff) {
            setAllowanceInput(dayOff.usesGlobalDefault ? "" : String(dayOff.allowance));
        }
    }, [dayOff]);

    const handleAdd = async () => {
        if (!startDate) {
            toast.error("Pick a start date.");
            return;
        }
        setAdding(true);
        try {
            await addDayOff({ agentId, startDate, endDate: endDate || startDate, note });
            toast.success("Day-off added.");
            setStartDate("");
            setEndDate("");
            setNote("");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not add day-off.");
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Remove this day-off?")) return;
        try {
            await deleteDayOff(id);
            toast.success("Day-off removed.");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not remove day-off.");
        }
    };

    const handleSaveAllowance = async () => {
        setSavingAllowance(true);
        try {
            // Empty string clears the override → falls back to the global default.
            await setAgentAllowance(agentId, allowanceInput === "" ? null : allowanceInput);
            toast.success("Allowance updated.");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not update allowance.");
        } finally {
            setSavingAllowance(false);
        }
    };

    if (!dayOff) return null;

    const remaining = dayOff.remaining;
    const remainingColor =
        remaining < 0 ? "text-red-600" : remaining <= 3 ? "text-amber-600" : "text-green-600";

    return (
        <div className="mt-6 border-t border-gray-100 pt-5">
            <div className="flex items-center gap-2 mb-3">
                <Umbrella className="h-4 w-4 text-sky-600" />
                <span className="text-sm font-semibold text-gray-800">
                    Day-offs — {fmtMonthLabel(dayOff.month)}
                </span>
            </div>

            {/* Allowance summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Allowance</div>
                    <div className="text-lg font-bold text-gray-900">{dayOff.allowance}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Used</div>
                    <div className="text-lg font-bold text-gray-900">{dayOff.used}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-500">Remaining</div>
                    <div className={`text-lg font-bold ${remainingColor}`}>{remaining}</div>
                </div>
            </div>

            {/* Per-agent allowance override */}
            <div className="flex items-end gap-2 mb-4">
                <div className="flex-1 max-w-[220px]">
                    <Label htmlFor="allowance" className="text-xs">
                        Monthly allowance for this agent
                    </Label>
                    <Input
                        id="allowance"
                        type="number"
                        min="0"
                        value={allowanceInput}
                        onChange={(e) => setAllowanceInput(e.target.value)}
                        placeholder={`Default (${dayOff.usesGlobalDefault ? dayOff.allowance : "global"})`}
                    />
                </div>
                <Button variant="outline" onClick={handleSaveAllowance} disabled={savingAllowance}>
                    {savingAllowance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <span className="text-[11px] text-gray-400 pb-2">
                    {dayOff.usesGlobalDefault ? "Using global default. Empty = keep default." : "Custom. Clear to use default."}
                </span>
            </div>

            {/* Add day-off form */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                    <div>
                        <Label className="text-xs">From</Label>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs">To (optional)</Label>
                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div>
                        <Label className="text-xs">Note (optional)</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Vacation" />
                    </div>
                    <Button onClick={handleAdd} disabled={adding} className="bg-sky-600 hover:bg-sky-700">
                        {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                        Add
                    </Button>
                </div>
            </div>

            {/* Day-off list */}
            <div className="border border-gray-100 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Dates</TableHead>
                            <TableHead className="text-center">Days</TableHead>
                            <TableHead>Note</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(dayOff.dayOffs || []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-gray-400 py-6">
                                    No day-offs recorded for {fmtMonthLabel(dayOff.month)}.
                                </TableCell>
                            </TableRow>
                        ) : (
                            dayOff.dayOffs.map((d) => (
                                <TableRow key={d._id}>
                                    <TableCell className="text-gray-800">
                                        {d.startDate === d.endDate
                                            ? fmtDateLabel(d.startDate)
                                            : `${fmtDateLabel(d.startDate)} – ${fmtDateLabel(d.endDate)}`}
                                    </TableCell>
                                    <TableCell className="text-center">{d.daysInMonth ?? d.days}</TableCell>
                                    <TableCell className="text-gray-600">{d.note || "—"}</TableCell>
                                    <TableCell className="text-right">
                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(d._id)} title="Remove">
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

/* --------------------------- Main page --------------------------- */
export function WorkTracking() {
    const { user } = useAuth();
    const isAdmin = user?.position?.role === "admin";
    const monthOptions = buildMonthOptions();
    const [month, setMonth] = useState(monthOptions[0].value);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [defaultInput, setDefaultInput] = useState("");
    const [savingDefault, setSavingDefault] = useState(false);
    const [schedStart, setSchedStart] = useState("");
    const [schedEnd, setSchedEnd] = useState("");
    const [schedGrace, setSchedGrace] = useState("");
    const [savingSched, setSavingSched] = useState(false);

    const load = useCallback(async (m) => {
        setLoading(true);
        try {
            const res = await getAdminOverview(m);
            setOverview(res?.data || null);
            if (res?.data?.defaultDayOffAllowance !== undefined) {
                setDefaultInput(String(res.data.defaultDayOffAllowance));
            }
            if (res?.data?.schedule) {
                setSchedStart(res.data.schedule.start || "");
                setSchedEnd(res.data.schedule.end || "");
                setSchedGrace(String(res.data.schedule.graceMinutes ?? 0));
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not load work tracking.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(month);
    }, [month, load]);

    const handleSaveDefault = async () => {
        setSavingDefault(true);
        try {
            await setDefaultAllowance(defaultInput);
            toast.success("Default allowance updated.");
            load(month);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not update default allowance.");
        } finally {
            setSavingDefault(false);
        }
    };

    const handleSaveSchedule = async () => {
        setSavingSched(true);
        try {
            await setWorkSchedule({ start: schedStart, end: schedEnd, graceMinutes: schedGrace });
            toast.success("Work schedule updated.");
            load(month);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not update schedule.");
        } finally {
            setSavingSched(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-6 text-center text-gray-500">
                You don't have access to this page.
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Work Tracking</h1>
                    <p className="text-sm text-gray-500">
                        Monitor agent attendance and working hours.
                        {overview?.timezone ? ` Timezone: ${overview.timezone}.` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <LiveClock timezone={overview?.timezone} />
                    <div className="w-48">
                        <Select options={monthOptions} value={month} onChange={setMonth} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatCard icon={Users} label="Staff tracked" value={overview?.agentCount ?? 0} />
                <StatCard icon={Activity} label="Working now" value={overview?.workingNow ?? 0} color="green" />
                <StatCard icon={Clock} label="Total hours (month)" value={fmtHours((overview?.totalHours || 0) * 3600)} color="sky" />
            </div>

            {/* Global default day-off allowance */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6 flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-2">
                    <CalendarX className="h-5 w-5 text-sky-600" />
                    <div>
                        <div className="text-sm font-medium text-gray-800">Default monthly day-off allowance</div>
                        <div className="text-[11px] text-gray-400">
                            Applies to agents without a custom allowance. Resets each month.
                        </div>
                    </div>
                </div>
                <div className="ml-auto flex items-end gap-2">
                    <div className="w-28">
                        <Label htmlFor="defaultAllowance" className="text-xs">
                            Days / month
                        </Label>
                        <Input
                            id="defaultAllowance"
                            type="number"
                            min="0"
                            value={defaultInput}
                            onChange={(e) => setDefaultInput(e.target.value)}
                        />
                    </div>
                    <Button variant="outline" onClick={handleSaveDefault} disabled={savingDefault}>
                        {savingDefault ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        Save
                    </Button>
                </div>
            </div>

            {/* Work schedule (for lateness tracking) */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6 flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-2">
                    <AlarmClock className="h-5 w-5 text-red-600" />
                    <div>
                        <div className="text-sm font-medium text-gray-800">Work schedule</div>
                        <div className="text-[11px] text-gray-400">
                            Used to flag late arrivals and early departures{overview?.timezone ? ` (${overview.timezone})` : ""}.
                        </div>
                    </div>
                </div>
                <div className="ml-auto flex items-end gap-2 flex-wrap">
                    <div className="w-28">
                        <Label htmlFor="schedStart" className="text-xs">Start</Label>
                        <Input id="schedStart" type="time" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} />
                    </div>
                    <div className="w-28">
                        <Label htmlFor="schedEnd" className="text-xs">End</Label>
                        <Input id="schedEnd" type="time" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} />
                    </div>
                    <div className="w-28">
                        <Label htmlFor="schedGrace" className="text-xs">Grace (min)</Label>
                        <Input id="schedGrace" type="number" min="0" value={schedGrace} onChange={(e) => setSchedGrace(e.target.value)} />
                    </div>
                    <Button variant="outline" onClick={handleSaveSchedule} disabled={savingSched}>
                        {savingSched ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        Save
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-20 text-gray-400">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Agent</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead className="text-center">Status</TableHead>
                                <TableHead className="text-right">Today</TableHead>
                                <TableHead className="text-right">Month total</TableHead>
                                <TableHead className="text-center">Days</TableHead>
                                <TableHead className="text-center">Avg start</TableHead>
                                <TableHead className="text-center">Avg end</TableHead>
                                <TableHead className="text-center">Late days</TableHead>
                                <TableHead className="text-center">Day-offs (month)</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(overview?.agents || []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="text-center text-gray-400 py-8">
                                        No staff found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                overview.agents.map((a) => (
                                    <TableRow
                                        key={a.agentId}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => setSelectedAgent(a.agentId)}
                                    >
                                        <TableCell>
                                            <div className="font-medium text-gray-900">
                                                {`${a.firstName} ${a.lastName}`.trim() || a.email}
                                            </div>
                                            <div className="text-xs text-gray-400">{a.email}</div>
                                        </TableCell>
                                        <TableCell className="text-gray-600">{a.position || a.role}</TableCell>
                                        <TableCell className="text-center">
                                            {a.isWorking ? (
                                                <Badge className="bg-green-100 text-green-700">Working</Badge>
                                            ) : (
                                                <Badge className="bg-gray-100 text-gray-500">Offline</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {a.todaySeconds ? fmtDuration(a.todaySeconds) : "—"}
                                        </TableCell>
                                        <TableCell className="text-right font-medium tabular-nums">
                                            {fmtDuration(a.totalSeconds)}
                                        </TableCell>
                                        <TableCell className="text-center">{a.daysWorked}</TableCell>
                                        <TableCell className="text-center">{a.avgStart || "—"}</TableCell>
                                        <TableCell className="text-center">{a.avgEnd || "—"}</TableCell>
                                        <TableCell className="text-center">
                                            {a.lateDays ? (
                                                <span className="text-red-600 font-medium" title={`${a.totalLateMinutes} min total late`}>
                                                    {a.lateDays}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">0</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <span
                                                className={
                                                    a.dayOffsRemaining < 0
                                                        ? "text-red-600 font-medium"
                                                        : a.dayOffsRemaining <= 3
                                                        ? "text-amber-600"
                                                        : "text-gray-700"
                                                }
                                                title={`${a.dayOffsUsed} used of ${a.dayOffAllowance} allowed`}
                                            >
                                                {a.dayOffsUsed} / {a.dayOffAllowance}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>

            <AgentDetailModal
                open={!!selectedAgent}
                onClose={() => {
                    setSelectedAgent(null);
                    load(month);
                }}
                agentId={selectedAgent}
                month={month}
            />
        </div>
    );
}

export default WorkTracking;
